import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { Asset, AssetParams, CreateAssetDTO, UpdateAssetDTO } from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';

export function useAssets(initialParams?: AssetParams) {
  const { inventoryRepo } = useServices();
  const [data, setData] = useState<PaginatedResponse<Asset> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<AssetParams>(initialParams || { page: 1, limit: 20 });

  const fetchAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryRepo.getAssets(params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar activos');
    } finally {
      setLoading(false);
    }
  }, [inventoryRepo, params]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  const search = useCallback((searchTerm: string) => {
    setParams(prev => ({ ...prev, search: searchTerm, page: 1 }));
  }, []);

  const filterByCategory = useCallback((categoryId: string | undefined) => {
    setParams(prev => ({ ...prev, categoryId, page: 1 }));
  }, []);

  const filterByStatus = useCallback((status: AssetParams['status']) => {
    setParams(prev => ({ ...prev, status, page: 1 }));
  }, []);

  const filterByMaintenanceDue = useCallback((maintenanceDue: boolean | undefined) => {
    setParams(prev => ({ ...prev, maintenanceDue, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const createAsset = useCallback(async (data: CreateAssetDTO) => {
    const newAsset = await inventoryRepo.createAsset(data);
    fetchAssets();
    return newAsset;
  }, [inventoryRepo, fetchAssets]);

  const updateAsset = useCallback(async (id: string, data: UpdateAssetDTO) => {
    const updated = await inventoryRepo.updateAsset(id, data);
    fetchAssets();
    return updated;
  }, [inventoryRepo, fetchAssets]);

  const deleteAsset = useCallback(async (id: string) => {
    await inventoryRepo.deleteAsset(id);
    fetchAssets();
  }, [inventoryRepo, fetchAssets]);

  const getAssetById = useCallback(async (id: string) => {
    return await inventoryRepo.getAssetById(id);
  }, [inventoryRepo]);

  const getAssetQR = useCallback(async (id: string) => {
    return await inventoryRepo.getAssetQR(id);
  }, [inventoryRepo]);

  return {
    assets: data?.data || [],
    pagination: data?.meta,
    loading,
    error,
    params,
    search,
    filterByCategory,
    filterByStatus,
    filterByMaintenanceDue,
    goToPage,
    createAsset,
    updateAsset,
    deleteAsset,
    getAssetById,
    getAssetQR,
    refresh: fetchAssets,
  };
}
